import { useRef, useState } from "react";
import { Sparkles, Loader2, FileArchive } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** AI extraction from a single document → auto-fills the open form. */
export const ExtractButton = ({ module, fields, onExtracted }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const run = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("fields", JSON.stringify(
        fields.filter((f) => f.type !== "file").map((f) => ({
          name: f.name, label: f.label, options: f.options,
        }))));
      const { data } = await api.post(`/extract/${module}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onExtracted({ ...data.data, attachment: data.attachment });
      toast.success(`Read ${Object.keys(data.data || {}).length} fields from ${file.name}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm:col-span-2 rounded-md border border-dashed border-[#002FA7]/40 bg-[#002FA7]/[0.04] p-3 flex items-center gap-3">
      <Sparkles className="h-4 w-4 text-[#002FA7] shrink-0" />
      <div className="flex-1">
        <p className="text-sm text-[#111827]">Auto-fill from a document</p>
        <p className="text-xs text-[#6B7280]">Upload the report, requisition or printout — LabTrack reads it and fills the fields below.</p>
      </div>
      <input ref={ref} type="file" accept=".pdf,image/*,.txt,.csv" className="hidden" data-testid="extract-file-input"
        onChange={(e) => { run(e.target.files?.[0]); e.target.value = ""; }} />
      <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy}
        data-testid="extract-btn" className="shrink-0">
        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading…</> : "Read & auto-fill"}
      </Button>
    </div>
  );
};

/** Bulk / ZIP upload with background extraction and progress polling. */
export const BulkUploadButton = ({ module, fields, onDone }) => {
  const ref = useRef(null);
  const [job, setJob] = useState(null);

  const poll = (jobId) => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/bulk-jobs/${jobId}`);
        setJob(data);
        if (data.status === "completed") {
          clearInterval(id);
          toast.success(`Logged ${data.succeeded} of ${data.total} documents${data.failed ? `, ${data.failed} failed` : ""}`);
          onDone?.();
          setTimeout(() => setJob(null), 6000);
        }
      } catch (e) {
        clearInterval(id);
      }
    }, 2500);
  };

  const start = async (files) => {
    if (!files?.length) return;
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("fields", JSON.stringify(
        fields.filter((f) => f.type !== "file").map((f) => ({
          name: f.name, label: f.label, options: f.options,
        }))));
      const { data } = await api.post(`/bulk-upload/${module}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(data);
      toast.success(`Reading ${data.total} document(s) in the background…`);
      poll(data.job_id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <input ref={ref} type="file" multiple accept=".pdf,image/*,.txt,.csv,.zip" className="hidden"
        data-testid="bulk-upload-input" onChange={(e) => { start(e.target.files); e.target.value = ""; }} />
      <Button variant="outline" onClick={() => ref.current?.click()} data-testid="bulk-upload-btn">
        <FileArchive className="h-4 w-4 mr-2 text-[#002FA7]" /> Bulk / ZIP
      </Button>
      {job && (
        <div className="fixed bottom-4 left-4 z-50 rounded-md border border-[#E5E7EB] bg-white shadow-lg p-4 w-72"
          data-testid="bulk-job-progress">
          <p className="text-sm font-medium text-[#111827]">
            {job.status === "completed" ? "Bulk upload finished" : "Reading documents…"}
          </p>
          <div className="h-1.5 bg-[#E5E7EB] rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-[#002FA7] transition-all"
              style={{ width: `${Math.round(((job.processed || 0) / (job.total || 1)) * 100)}%` }} />
          </div>
          <p className="text-xs text-[#6B7280] mt-2" data-testid="bulk-job-status">
            {job.processed || 0}/{job.total} processed · {job.succeeded || 0} logged
            {job.failed ? ` · ${job.failed} failed` : ""}
          </p>
          {(job.errors || []).slice(0, 2).map((er) => (
            <p key={er.file} className="text-[10px] text-[#C92A2A] mt-1 truncate">{er.file}: {er.error}</p>
          ))}
        </div>
      )}
    </>
  );
};
